import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import apiService from 'services/api'
import { CompanyPageAccordion } from 'components/NewCompanyPage/CompanyPageAccordion'
import { DealCard } from 'components/NewCompanyPage/DealCard'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import { Deal } from 'models/deals'
import { MobileColors } from 'styles/mobileTokens'
import { PiHandshake } from 'react-icons/pi'

interface MobileDealsAccordionProps {
  chainProxyId: number
  doorProxyId?: number
}

export function MobileDealsAccordion({
  chainProxyId,
  doorProxyId,
}: MobileDealsAccordionProps) {
  const api = apiService()
  const navigate = useNavigate()
  const [dealToEdit, setDealToEdit] = useState<Deal>()

  const params = {
    ...(doorProxyId
      ? { door_proxy_id: doorProxyId, door_only: true }
      : { company: chainProxyId }),
    page_size: 50,
    include_ltv: true,
  }

  // Fetch deals for the company or door
  // When on a door page, only show door-specific deals (not company-wide deals)
  const [{ data: dealsResponse, isLoading: dealsLoading }, queryKey] =
    api.useGetDeals(params)

  // Fetch sales stages
  const { data: salesStages } = api.useGetSalesStages()

  // Fetch users for account owner names
  const { data: users } = api.useGetUsers()

  const deals = dealsResponse?.results || []

  // Handle deal edit
  const handleEditDeal = (deal: Deal) => {
    setDealToEdit(deal)
  }

  // Custom header with History icon
  const headerLeft = (
    <div className="flex items-center gap-2">
      <PiHandshake className="w-4 h-4 text-gray-500" />
      <span className="font-semibold text-sm">Deals</span>
      <span className="text-gray-500 text-sm">({deals.length})</span>
    </div>
  )

  return (
    <>
      <CompanyPageAccordion
        title=""
        customTitle={headerLeft}
        hideChevron={true}
        headerRight={
          <AllDealsLink
            onClick={() =>
              navigate(`/deals?contact_company_id=${chainProxyId}`)
            }
          >
            All Deals
          </AllDealsLink>
        }
      >
        <DealsContent>
          {dealsLoading ? (
            <LoadingText>Loading deals...</LoadingText>
          ) : deals.length === 0 ? (
            <EmptyStateCard>
              <EmptyStateContent>
                <EmptyStateTitle>No Deals Yet.</EmptyStateTitle>
                <EmptyStateSubtitle>Ready to make a deal?</EmptyStateSubtitle>
              </EmptyStateContent>
            </EmptyStateCard>
          ) : (
            <CarouselContainer>
              <CarouselScroll>
                {deals.map((deal) => (
                  <CardWrapper key={deal.id}>
                    <DealCard
                      deal={deal}
                      salesStages={salesStages}
                      users={users}
                      onEdit={handleEditDeal}
                    />
                  </CardWrapper>
                ))}
              </CarouselScroll>
              {deals.length > 1 && (
                <ScrollHint>Scroll horizontally to see all deals →</ScrollHint>
              )}
            </CarouselContainer>
          )}
        </DealsContent>
      </CompanyPageAccordion>

      {/* Edit Deal Modal */}
      <EditDealModal
        onClose={() => setDealToEdit(undefined)}
        dealsTableQueryKey={queryKey}
        show={!!dealToEdit}
        deal={dealToEdit}
      />
    </>
  )
}

const AllDealsLink = styled.button`
  background: none;
  border: none;
  color: ${MobileColors.textLink};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`

const DealsContent = styled.div`
  padding-top: 16px;
`

const LoadingText = styled.div`
  text-align: center;
  padding: 32px 0;
  color: ${MobileColors.textSecondary};
`

const EmptyStateCard = styled.div`
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${MobileColors.borderDefault};
  background: ${MobileColors.borderDefault};
  padding-left: 8px;
`

const EmptyStateContent = styled.div`
  background: white;
  border-radius: 0 8px 8px 0;
  padding: 12px;
`

const EmptyStateTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${MobileColors.textPrimary};
  margin: 0;
  line-height: 1.2;
`

const EmptyStateSubtitle = styled.p`
  font-size: 12px;
  font-weight: 300;
  color: ${MobileColors.textPrimary};
  margin: 4px 0 0 0;
  line-height: 1.2;
`

const CarouselContainer = styled.div`
  position: relative;
`

const CarouselScroll = styled.div`
  overflow-x: auto;
  padding-bottom: 8px;
  display: flex;
  gap: 16px;
`

const CardWrapper = styled.div`
  flex-shrink: 0;
`

const ScrollHint = styled.div`
  font-size: 12px;
  color: ${MobileColors.textSecondary};
  text-align: center;
  margin-top: 8px;
`
